// frontend/src/api/backendAiService.js
// New service that calls backend API instead of Anthropic directly
// Falls back to direct Firestore queries if backend API fails
import { auth, db } from '../utils/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

/**
 * Get Firebase ID token for authentication
 */
async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
}

/**
 * Base AI Service Class - Calls Backend API
 */
class BaseAIService {
  constructor(salonId, endpoint) {
    this.salonId = salonId;
    this.endpoint = endpoint;
    this.conversationHistory = [];
    // Fire-and-forget: ping the health endpoint immediately so the Vercel
    // function cold-starts now rather than when the user sends their first message.
    fetch(`${API_BASE_URL}/api/ai/health`).catch(() => {});
  }

  /**
   * Check if context has meaningful data (at least one non-empty array)
   */
  isContextEmpty(context) {
    if (!context || typeof context !== 'object') return true;
    return Object.values(context).every(val =>
      !val || (Array.isArray(val) && val.length === 0)
    );
  }

  /**
   * Fallback: Query Firestore directly using client SDK
   * Mirrors the backend /api/ai/context/:module queries
   */
  async getContextViaFirestore() {
    try {
      console.log(`Fallback: Fetching ${this.endpoint} context directly from Firestore for salon: ${this.salonId}`);

      if (this.endpoint === 'bookings') {
        const [bookings, services, staff, customers] = await Promise.all([
          getDocs(query(collection(db, 'appointments'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'services'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'staff'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'customers'), where('salonId', '==', this.salonId)))
        ]);
        return {
          bookings: bookings.docs.map(doc => ({ id: doc.id, ...doc.data() })).slice(0, 100),
          services: services.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          staff: staff.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          customers: customers.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        };
      } else if (this.endpoint === 'promotions') {
        const [customers, promotions, bookings, services] = await Promise.all([
          getDocs(query(collection(db, 'customers'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'promotions'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'appointments'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'services'), where('salonId', '==', this.salonId)))
        ]);
        return {
          customers: customers.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          pastPromotions: promotions.docs.map(doc => ({ id: doc.id, ...doc.data() })).slice(0, 20),
          bookings: bookings.docs.map(doc => ({ id: doc.id, ...doc.data() })).slice(0, 100),
          services: services.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        };
      } else if (this.endpoint === 'communications') {
        const [customers, emails, pushNotifications, services] = await Promise.all([
          getDocs(query(collection(db, 'customers'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'mail'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'pushNotifications'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'services'), where('salonId', '==', this.salonId)))
        ]);
        const emailCampaigns = emails.docs.map(doc => ({ id: doc.id, type: 'email', ...doc.data() }));
        const pushCampaigns = pushNotifications.docs.map(doc => ({ id: doc.id, type: 'push', ...doc.data() }));
        return {
          customers: customers.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          pastCampaigns: [...emailCampaigns, ...pushCampaigns],
          services: services.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        };
      } else if (this.endpoint === 'reports') {
        const [transactions, customers, services, staff] = await Promise.all([
          getDocs(query(collection(db, 'transactions'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'customers'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'services'), where('salonId', '==', this.salonId))),
          getDocs(query(collection(db, 'staff'), where('salonId', '==', this.salonId)))
        ]);
        return {
          transactions: transactions.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          customers: customers.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          services: services.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          staff: staff.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        };
      }

      return {};
    } catch (error) {
      console.error(`Firestore fallback failed for ${this.endpoint}:`, error);
      return {};
    }
  }

  async getSalonContext() {
    try {
      // Get auth token
      const token = await getAuthToken();

      console.log(`Fetching context for ${this.endpoint} AI, salon: ${this.salonId}`);

      // Call backend API to get context
      const response = await fetch(`${API_BASE_URL}/api/ai/context/${this.endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          salonId: this.salonId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const context = data.context || {};

      // If backend returned empty context, fall back to direct Firestore queries
      if (this.isContextEmpty(context)) {
        console.log(`Backend returned empty context for ${this.endpoint}, trying Firestore fallback...`);
        return await this.getContextViaFirestore();
      }

      return context;

    } catch (error) {
      console.error(`Error fetching context for ${this.endpoint} AI:`, error);
      // Fall back to direct Firestore queries instead of returning empty
      console.log(`Backend API failed for ${this.endpoint}, trying Firestore fallback...`);
      return await this.getContextViaFirestore();
    }
  }

  /**
   * Trim conversation history to prevent context bloat.
   * Keeps the most recent messages up to MAX_HISTORY_MESSAGES,
   * always starting from a plain user text message (never a tool_result).
   */
  trimConversationHistory(history) {
    const MAX_HISTORY_MESSAGES = 20;
    if (history.length <= MAX_HISTORY_MESSAGES) return history;

    let trimmed = history.slice(history.length - MAX_HISTORY_MESSAGES);

    // Ensure we start with a plain user text message, not a tool_result block
    while (trimmed.length > 0) {
      const first = trimmed[0];
      if (first.role === 'user' && typeof first.content === 'string') break;
      trimmed = trimmed.slice(1);
    }

    return trimmed;
  }

  async _doSendMessage(userMessage, token) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 270000); // 270s — just under Vercel's 300s max

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/${this.endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          salonId: this.salonId,
          message: userMessage,
          conversationHistory: this.conversationHistory
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async sendMessage(userMessage) {
    try {
      const token = await getAuthToken();

      console.log(`Sending message to ${this.endpoint} for salon: ${this.salonId}`);

      let data;
      try {
        data = await this._doSendMessage(userMessage, token);
      } catch (firstError) {
        // Retry once on 500 — likely a cold-start timeout
        if (firstError.status === 500 || firstError.name === 'AbortError') {
          console.warn(`${this.endpoint} AI first attempt failed (${firstError.message}), retrying...`);
          // Short pause so the instance has time to warm up
          await new Promise(resolve => setTimeout(resolve, 2000));
          // Refresh token in case it expired during the wait
          const freshToken = await getAuthToken();
          data = await this._doSendMessage(userMessage, freshToken);
        } else {
          throw firstError;
        }
      }

      // Update conversation history so the next question has full context
      this.conversationHistory = this.trimConversationHistory(data.conversationHistory || []);

      return {
        message: data.message,
        reportData: data.reportData || null,
        htmlEmail: data.htmlEmail || null,
        textEmail: data.textEmail || null,
        emailSubject: data.emailSubject || null,
        whatsappMessage: data.whatsappMessage || null,
        whatsappImage: data.whatsappImage || null,
        pushTitle: data.pushTitle || null,
        pushBody: data.pushBody || null,
        pushImage: data.pushImage || null,
        dailyUsage: data.dailyUsage || null
      };

    } catch (error) {
      console.error(`Error in ${this.endpoint} AI:`, error);
      throw error;
    }
  }

  async storeFeedback(messageId, feedback) {
    // Placeholder for feedback storage
    console.log('Feedback stored:', { messageId, feedback, timestamp: new Date() });
    return true;
  }

  reset() {
    this.conversationHistory = [];
  }
}

/**
 * AI agents that carry out instructions (Bookings AI, Loyalty AI, Communications AI). Unlike the Q&A services they never retry
 * automatically, because a retried request could repeat a change that already went through.
 */
class AgentService extends BaseAIService {

  // The agent may need several rounds of questions to collect everything for a task, and each round carries its
  // tool calls, so keep far more history than the Q&A services or the admin's earlier answers get dropped.
  trimConversationHistory(history) {
    const MAX_HISTORY_MESSAGES = 80;
    if (history.length <= MAX_HISTORY_MESSAGES) return history;
    let trimmed = history.slice(history.length - MAX_HISTORY_MESSAGES);
    while (trimmed.length > 0 && !(trimmed[0].role === 'user' && typeof trimmed[0].content === 'string')) {
      trimmed = trimmed.slice(1);
    }
    return trimmed;
  }

  async sendMessage(userMessage) {
    const token = await getAuthToken();
    const data = await this._doSendMessage(userMessage, token);
    this.conversationHistory = this.trimConversationHistory(data.conversationHistory || []);
    return {
      message: data.message,
      actions: data.actions || [],
      pendingActions: data.pendingActions || [],
      drafts: data.drafts || [],
      reports: data.reports || [],
      campaigns: data.campaigns || [],
      dailyUsage: data.dailyUsage || null
    };
  }

  async resolveAction(actionId, decision) {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/ai/${this.endpoint}/actions/${encodeURIComponent(actionId)}/${decision}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ salonId: this.salonId })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.message || `HTTP error! status: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return data;
  }
}

export class BookingsAgentService extends AgentService {
  constructor(salonId) {
    super(salonId, 'bookings-agent');
  }
}

export class LoyaltyAgentService extends AgentService {
  constructor(salonId) {
    super(salonId, 'loyalty-agent');
  }
}

export class CommunicationsAgentService extends AgentService {
  constructor(salonId) {
    super(salonId, 'communications-agent');
  }
}

export class ReportsAgentService extends AgentService {
  constructor(salonId) {
    super(salonId, 'reports-agent');
  }
}

export class CoreAgentService extends AgentService {
  constructor(salonId) {
    super(salonId, 'core-agent');
  }
}

export class CustomerPortalAgentService {
  constructor(salonId) {
    this.salonId = salonId;
    this.conversationHistory = [];
    fetch(`${API_BASE_URL}/api/ai/health`).catch(() => {});
  }

  trimHistory(history) {
    const MAX = 40;
    if (history.length <= MAX) return history;
    let trimmed = history.slice(history.length - MAX);
    while (trimmed.length > 0 && !(trimmed[0].role === 'user' && typeof trimmed[0].content === 'string')) {
      trimmed = trimmed.slice(1);
    }
    return trimmed;
  }

  async sendMessage(userMessage) {
    const token = await getAuthToken();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 270000);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/customer-portal-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          salonId: this.salonId,
          message: userMessage,
          conversationHistory: this.conversationHistory
        }),
        signal: controller.signal
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const details = errData.details ? ` (${errData.details})` : '';
        const err = new Error((errData.message || `HTTP error! status: ${response.status}`) + details);
        err.status = response.status;
        throw err;
      }
      const data = await response.json();
      this.conversationHistory = this.trimHistory(data.conversationHistory || []);
      return { message: data.message, dailyUsage: data.dailyUsage || null };
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Loyalty AI Service
 */
export class LoyaltyAIService extends BaseAIService {
  constructor(salonId) {
    super(salonId, 'loyalty');
  }

  // Keep these methods for backward compatibility
  extractParameters(message) {
    return {};
  }

  validateParameters() {
    return false;
  }

  getMissingParameters() {
    return [];
  }

  generateDescription() {
    return '';
  }
}

/**
 * Communications AI Service
 */
export class CommunicationsAIService extends BaseAIService {
  constructor(salonId) {
    super(salonId, 'communications');
  }
}

/**
 * Reports AI Service
 */
/**
 * Fetch today's AI token usage for a salon.
 * Returns byModule breakdown, totalTokens, limitExceeded flag.
 */
export async function getDailyTokenUsage(salonId) {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/ai/daily-usage?salonId=${encodeURIComponent(salonId)}`,
      { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (!response.ok) return { byModule: {}, totalTokens: 0, limitExceeded: false, dailyLimit: 10000 };
    return await response.json();
  } catch {
    return { byModule: {}, totalTokens: 0, limitExceeded: false, dailyLimit: 10000 };
  }
}

/**
 * Transform a customer's hair color or style using AI
 */
export async function transformCustomerHair({ salonId, customerPhotoUrl, referenceImageBase64, referenceMediaType, transformType }) {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/api/ai/hair-transform`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      salonId,
      customerPhotoUrl,
      referenceImageBase64,
      referenceMediaType,
      transformType
    })
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.message || errData.details || `Request failed: ${response.status}`);
  }

  return await response.json();
}

export default {
  BookingsAgentService,
  LoyaltyAIService,
  CommunicationsAIService
};
